import { CalculateAllowanceResponse } from '@app-types';
import termsOfUsePdfFile from '@assets/termsOfUse.pdf';
import policyPdfFile from '@assets/privacyPolicy.pdf';
import { AlertTriangleIcon, AlertOctagonIcon } from '@icons';
import { Box, Link, Stack, Typography, useTheme } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Amount from './Amount';
import { AmountBox, CheckboxesBox, NoteBox, StyledButton, StyledCheckboxController, SubmissionFormBox } from './styles';
import { SubmissionRadioValue } from '../../types';

interface ISubmissionFormFooterProps {
  className?: string;
  data: CalculateAllowanceResponse | undefined;
  isSubmitting: boolean;
  checkOngoingBenefits: () => void;
  setScrollActivated: (isScrollActivated: boolean) => void;
  isOngoingBenefitsValuesUndefinedOrEmpty: boolean;
}

const SubmissionFormFooter: FC<ISubmissionFormFooterProps> = ({
  className,
  data,
  isSubmitting,
  checkOngoingBenefits,
  setScrollActivated,
  isOngoingBenefitsValuesUndefinedOrEmpty,
}) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const [formErrors, setFormErrors] = useState<string | null>(null);

  const { control, watch, formState, setValue } = useFormContext();
  const { errors } = formState;

  const confirmAccurancy = watch('confirmAccurancy');
  const conseptTheProcessing = watch('conseptTheProcessing');

  const ankers = {
    documents: [] as string[],
    inputs: [] as string[],
    selects: [] as string[],
  };

  const updateAnkers = (anker: string, type: string) => {
    switch (type) {
      case 'is-defined':
        ankers.documents.push(anker);
        break;
      case 'optionality':
        ankers.inputs.push(anker);
        break;
      case 'select-is-empty':
        ankers.selects.push(anker);
        break;
      default:
        break;
    }
  };

  const createErrorsMessage = () => {
    let linksMessage = '';

    Object.entries(ankers).forEach(([key, value]) => {
      if (value.length === 0)
        return;

      const messageId = formatMessage({ id: `submissionCreation.validation.errorType.${key}` });
      linksMessage = `${linksMessage}</br><a href=#${value[0]}>${messageId}</a>`;
    });

    return linksMessage
      ? `${formatMessage({ id: 'submissionCreation.validation.common' })}</br></br>
        <b>${formatMessage({ id: 'submissionCreation.validation.errorType.title' })}</b>
        ${linksMessage}`
      : null;
  };

  const handleObjectErrors = (ankerKey: string, handledErrors: object, index: number = 0) => {
    if (!handledErrors)
      return;

    Object.entries(handledErrors).forEach(([key, value]) => {
      const anker = `${ankerKey}-${index}${key === 'supplementDocuments' ? '.supplementDocuments' : ''}`;
      const type = value.type || '';
      updateAnkers(anker, type);
    });
  };

  const handleArrayErrors = (key: string, handledErrors: object[]) => {
    handledErrors.forEach((error, index) => {
      handleObjectErrors(key, error, index);
    });
  };

  const handleError = (key: string, error: object) => {
    if (key === 'ongoingBenefit') {
      handleObjectErrors(key, error);
      return;
    }

    const anker = key;
    const type = (error as any)?.type || '';
    updateAnkers(anker, type);
  };

  const hasBenefitFields = watch([
    'isUserReceivedOngoingBenefit',
    'isUserReceivedOneOffBenefits',
    'isUserReceivedBackPaymentBenefits',
    'isUserReceivedHealthDamages',
    'isUserReceivedMotherChildFoundations',
    'isUserReceivedBackPaymentWagePensionBenefits',
    'isUserReceivedAdditionalBenefits',
  ]);

  const [
    hasSpouse,
    isThereSpouseHousehold,
    isThereSpouseMaintenance,
  ] = watch([
    'hasSpouse',
    'isThereSpouseHousehold',
    'isThereSpouseMaintenance',
  ]);

  const children = watch('children');

  const hasSpouseReason =
    hasSpouse === SubmissionRadioValue.Yes &&
    (isThereSpouseHousehold === SubmissionRadioValue.Yes || isThereSpouseMaintenance === SubmissionRadioValue.Yes);

  const hasChildrenReason = children?.length > 0;
  const hasOtherReasons = hasBenefitFields.some((benefit) => benefit === SubmissionRadioValue.Yes);

  const hasAtLeastOneReason = hasSpouseReason || hasChildrenReason || hasOtherReasons;

  useEffect(() =>{
    setValue('noOneReason', hasAtLeastOneReason);
  }, [hasAtLeastOneReason]);

  useEffect(() => {
    Object.keys(errors)
      .filter((key) => !['isUserReadDeclarationOfConsent', 'isUserReadTermsOfUse', 'noOneReason'].includes(key))
      .forEach((key) => {
        const currentErrors = errors[key];

        if (Array.isArray(currentErrors)) {
          handleArrayErrors(key, currentErrors);
        } else {
          handleError(key, currentErrors as object);
        }

        if (isOngoingBenefitsValuesUndefinedOrEmpty) {
          const anker = 'ongoingBenefit-0';
          const type = 'optionality';
          updateAnkers(anker, type);
        }
      });

    const errorsMessage = createErrorsMessage();
    setFormErrors(errorsMessage || null);
  }, [errors]);

  return (
    <SubmissionFormBox className={className}>
      <AmountBox>
        <Amount
          value={`${data?.data?.basicMonthlyAllowance.toFixed(2) || 0}`}
          title={formatMessage({ id: 'submissionCreation.footer.monthlyAllowance.title' })}
        />
        <Amount
          value={`${data?.data?.oneOffAllowance.toFixed(2) || 0}`}
          title={formatMessage({ id: 'submissionCreation.footer.oneOffAllowance.title' })}
        />
      </AmountBox>
      <NoteBox>
        <AlertTriangleIcon color={theme.palette.secondary.main} />
        <Typography fontSize={16} color={theme.palette.secondary.main} sx={{ lineHeight: 1.64, fontWeight: '700' }}>
          <FormattedMessage id="submissionCreation.footer.note" />
        </Typography>
      </NoteBox>
      <CheckboxesBox>
        <StyledCheckboxController
          name="confirmAccurancy"
          control={control}
          label={
            <div>
              {formatMessage({ id: 'submissionCreation.footer.firstCheckbox' })}
              &nbsp;
              <Link href={termsOfUsePdfFile} target="_blank" onClick={() => setValue('isUserReadTermsOfUse', true)}>
                {formatMessage({ id: 'submissionCreation.footer.readTermsOfUse' })}
              </Link>
            </div>
          }
        />
        <StyledCheckboxController
          name="conseptTheProcessing"
          control={control}
          label={
            <div>
              {formatMessage({ id: 'submissionCreation.footer.secondCheckbox' })}
              &nbsp;
              <Link href={policyPdfFile} target="_blank" onClick={() => setValue('isUserReadDeclarationOfConsent', true)}>
                {formatMessage({ id: 'submissionCreation.footer.readDeclarationOfConsent' })}
              </Link>
            </div>
          }
        />
      </CheckboxesBox>
      {(formErrors ||
        !!errors?.isUserReadTermsOfUse?.message ||
        !!errors?.isUserReadDeclarationOfConsent?.message ||
        !!errors?.noOneReason?.message
      ) && (
        <Stack sx={{
          mt: '30px',
          p: '30px',
          gap: 2,
          borderRadius: '8px',
          bgcolor: theme.palette.background.paper,
        }}>
          {[
            errors?.isUserReadTermsOfUse?.message,
            errors?.isUserReadDeclarationOfConsent?.message,
            errors?.noOneReason?.message,
            formErrors,
          ].filter(Boolean)
            .map((error, index) => (
              <Box key={index} sx={{ display: 'flex', gap: '8px' }}>
                <Box sx={{ flexShrink: 0 }}>
                  <AlertOctagonIcon color={theme.palette.error.main} />
                </Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: theme.palette.error.main,
                    '& > span': { fontWeight: '600', color: theme.palette.error.main },
                    '& > a': { color: theme.palette.error.main },
                  }}
                  dangerouslySetInnerHTML={{ __html: error as string }}
                  onClick={() => setScrollActivated(true)}
                />
              </Box>
            ))}
        </Stack>
      )}
      <StyledButton
        onClick={checkOngoingBenefits}
        type="submit"
        color="secondary"
        variant="contained"
        disabled={isSubmitting || !confirmAccurancy || !conseptTheProcessing}
        sx={{ mt: '30px' }}
      >
        <FormattedMessage id="submissionCreation.footer.button.submit" />
      </StyledButton>
    </SubmissionFormBox>
  );
};

export default SubmissionFormFooter;
